import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowRight,
  CheckCircle,
  Calculator,
  Wallet,
  Users,
  Receipt,
  PieChart,
  Megaphone,
  Settings,
  BarChart3,
  Target,
} from 'lucide-react';
import { LP_URL, SERVICES } from '../lib/utils';
import CTASection from '../components/shared/CTASection';
import { usePageMeta } from '../components/shared/SEO';

const iconMap = {
  Calculator, Wallet, Users, Receipt, PieChart, Megaphone, Settings, BarChart3, Target
};

const serviceDetails = {
  'contabilidade': {
    fullDescription: 'Nossa contabilidade tributária é especializada exclusivamente no setor médico. Entendemos as particularidades de cada tipo de atividade médica e estruturamos o modelo fiscal mais vantajoso para sua realidade.',
    benefits: [
      'Enquadramento tributário otimizado para sua atividade',
      'Planejamento fiscal preventivo',
      'Redução legal da carga tributária',
      'Acompanhamento de obrigações acessórias',
      'Relatórios gerenciais mensais',
    ],
  },
  'bpo-financeiro': {
    fullDescription: 'Terceirizamos todo o operacional financeiro da sua clínica, desde contas a pagar e receber até conciliação bancária e projeção de fluxo de caixa. Você mantém o controle estratégico, nós cuidamos da execução.',
    benefits: [
      'Contas a pagar e receber gerenciadas',
      'Conciliação bancária diária',
      'Fluxo de caixa projetado',
      'Relatórios financeiros automatizados',
      'Alertas de vencimentos e inconsistências',
    ],
  },
  'bpo-rh': {
    fullDescription: 'Gerenciamos toda a rotina de recursos humanos da sua equipe, garantindo conformidade trabalhista e eficiência nos processos de pessoas.',
    benefits: [
      'Processamento de folha de pagamento',
      'Gestão de admissões e demissões',
      'Controle de férias e afastamentos',
      'Compliance trabalhista',
      'Suporte em questões de RH',
    ],
  },
  'faturamento': {
    fullDescription: 'Otimizamos seu faturamento junto aos convênios, reduzindo glosas e garantindo que você receba por todos os serviços prestados.',
    benefits: [
      'Auditoria de contas antes do envio',
      'Redução de glosas',
      'Recurso de glosas negadas',
      'Acompanhamento de recebíveis',
      'Relatórios de performance de faturamento',
    ],
  },
  'rateio': {
    fullDescription: 'Calculamos e distribuímos receitas entre múltiplos profissionais de forma precisa e transparente, considerando critérios específicos de cada estrutura.',
    benefits: [
      'Cálculo automatizado de rateios',
      'Múltiplos critérios de divisão',
      'Transparência total nos cálculos',
      'Relatórios individuais por profissional',
      'Integração com faturamento',
    ],
  },
  'marketing': {
    fullDescription: 'Desenvolvemos estratégias de marketing médico dentro das normas do CFM, focadas em posicionamento e captação qualificada de pacientes.',
    benefits: [
      'Estratégia de presença digital',
      'Gestão de reputação online',
      'Produção de conteúdo especializado',
      'Campanhas de captação de pacientes',
      'Análise de resultados e ROI',
    ],
  },
  'gestao': {
    fullDescription: 'Assumimos a gestão integrada de toda a operação administrativa e financeira da sua clínica, liberando você para focar exclusivamente nos pacientes.',
    benefits: [
      'Integração de todos os processos',
      'Gestão de equipe administrativa',
      'Padronização de processos',
      'Melhoria contínua de operações',
      'Suporte consultivo estratégico',
    ],
  },
  'analytics': {
    fullDescription: 'Criamos dashboards e sistemas de indicadores personalizados para que você tenha visibilidade completa da performance da sua clínica.',
    benefits: [
      'Dashboards em tempo real',
      'KPIs financeiros e operacionais',
      'Análises de tendências',
      'Alertas automáticos',
      'Relatórios executivos periódicos',
    ],
  },
  'planejamento': {
    fullDescription: 'Construímos junto com você o plano estratégico da sua clínica, definindo metas, iniciativas e indicadores de acompanhamento.',
    benefits: [
      'Diagnóstico da situação atual',
      'Definição de visão e metas',
      'Plano de ação estruturado',
      'Acompanhamento de execução',
      'Revisões periódicas de estratégia',
    ],
  },
};

const ServicesPage = () => {
  usePageMeta({
    title: 'Serviços de Contabilidade e Backoffice para Clínicas Médicas | Apex Medical Hub',
    description: 'Contabilidade especializada, economia tributária, BPO financeiro, gestão de RH e operações para clínicas e consultórios médicos em todo o Brasil.',
    canonical: '/servicos',
  });

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-white pt-28 pb-10" data-testid="services-hero">
        <div className="container-apex">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#007f73] font-medium mb-3 text-sm uppercase tracking-wider">
              Nossos Serviços
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Soluções completas para clínicas e consultórios
            </h1>
            <p className="text-slate-600 text-lg">
              Do financeiro ao estratégico, oferecemos tudo que sua operação médica precisa para funcionar com eficiência e crescer com segurança.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-apex">
          <div className="space-y-16">
            {SERVICES.map((service, index) => {
              const Icon = iconMap[service.icon];
              const details = serviceDetails[service.id];
              const isEven = index % 2 === 0;

              return (
                <div
                  key={service.id}
                  id={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? 'lg:grid-flow-dense' : ''
                  }`}
                  data-testid={`service-section-${service.id}`}
                >
                  <div className={!isEven ? 'lg:col-start-2' : ''}>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1e4e7e]/10 to-[#007f73]/10 mb-6">
                      <Icon className="w-8 h-8 text-[#1e4e7e]" />
                    </div>
                    <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-slate-600 text-lg mb-6">
                      {details?.fullDescription || service.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {details?.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-[#007f73] mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild className="btn-primary rounded-md px-8">
                      <a href={LP_URL} target="_blank" rel="noopener noreferrer">
                        Solicitar diagnóstico
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                  <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
                    <Card className="overflow-hidden border-0 shadow-xl">
                      <CardContent className="p-0">
                        <div className="aspect-[4/3] bg-gradient-to-br from-[#1e4e7e] to-[#007f73] flex items-center justify-center">
                          <Icon className="w-24 h-24 text-white/20" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Pronto para transformar sua operação?"
        description="Agende uma conversa sem compromisso e descubra como a Apex pode ajudar sua clínica."
        buttonText="Falar com especialista"
      />
    </div>
  );
};

export default ServicesPage;
